import React, { useState } from "react";
import { ArrowRight, Infinity as InfinityIcon } from "lucide-react";
import { useHomeCta } from "../hooks/useHomeCta";
import { openAgent } from "../utils/openAgent";

const STEPS_DATA = [
  {
    title: "Software",
    text: "Systems that store information, manage processes, and execute defined tasks.",
    color: "#00A870",
    borderColor: "#2DD4BF",
    bgColor: "#E6F8F3",
    img: "/images/step-software.png",
  },
  {
    title: "Automation",
    text: "Connect processes and automate predictable work across systems and workflows.",
    color: "#9333EA",
    borderColor: "#C084FC",
    bgColor: "#F5EEFD",
    icon: () => (
      <InfinityIcon className="h-7 w-7" strokeWidth={2.4} />
    ),
  },
  {
    title: "Agents",
    text: "AI that understands goals, reasons through context, uses tools, and takes action.",
    color: "#2563EB",
    borderColor: "#60A5FA",
    bgColor: "#EFF6FF",
    img: "/images/step-agents.png",
  },
  {
    title: "Digital Workers",
    text: "Persistent AI capabilities that perform increasingly complex cognitive work across systems.",
    color: "#F59E0B",
    borderColor: "#FBBF24",
    bgColor: "#FFF7ED",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-[26px] w-[26px]">
        <rect x="2.5" y="3.5" width="19" height="13" rx="2.5" />
        <path d="m9 10 2 2 4-4" />
        <path d="M12 16.5v4" />
        <path d="M8 20.5h8" />
      </svg>
    ),
  },
  {
    title: "Cognitive Enterprise",
    text: "People, AI agents, knowledge, and systems working together as one intelligent enterprise.",
    color: "#EF4444",
    borderColor: "#F87171",
    bgColor: "#FEE2E2",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-[26px] w-[26px]">
        <rect x="2.5" y="7" width="19" height="13.5" rx="2.5" />
        <path d="M16 7V4.5a1.5 1.5 0 0 0-1.5-1.5h-5A1.5 1.5 0 0 0 8 4.5V7" />
        <path d="M2.5 12.5h7M14.5 12.5h7" />
        <rect x="9.5" y="10.5" width="5" height="4" rx="1" />
      </svg>
    ),
  },
];

const StepCard = ({ s }) => {
  const [isHovered, setIsHovered] = useState(false);
  const IconComponent = s.icon;
  const activeColor = s.borderColor || s.color;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="flex w-full flex-1 flex-col items-center justify-start gap-4 rounded-[20px] bg-white p-6 text-center transition-all duration-300 hover:-translate-y-1 xl:min-h-[290px] cursor-pointer"
      style={{
        border: `1.5px solid ${isHovered ? activeColor : "#F1F5F9"}`,
        boxShadow: isHovered
          ? `0 12px 30px -4px ${activeColor}30, 0 4px 12px -2px rgba(0,0,0,0.04)`
          : "0 4px 25px rgba(0,0,0,0.03)",
      }}
    >
      <div
        className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full overflow-hidden transition-transform duration-300"
        style={{
          backgroundColor: s.bgColor,
          color: s.color,
          transform: isHovered ? "scale(1.06)" : "scale(1)",
        }}
      >
        {s.img ? (
          <img
            src={s.img}
            alt={s.title}
            className="h-full w-full object-cover"
          />
        ) : (
          IconComponent && <IconComponent />
        )}
      </div>

      <div className="flex flex-col items-center gap-2">
        <span className="text-[16px] font-bold leading-snug text-[#0F172A]">
          {s.title}
        </span>
        <span className="text-[13px] font-normal leading-[21px] text-[#64748B]">
          {s.text}
        </span>
      </div>
    </div>
  );
};

const TomorrowSection = () => {
  const goToHomeCta = useHomeCta();

  return (
    <section id="cognitive" className="w-full bg-white">
      <div className="mx-auto max-w-[1440px] px-6 py-20 lg:px-[70px] lg:py-24">
        {/* Centered Heading Section */}
        <div className="mx-auto mb-16 flex max-w-[1100px] flex-col items-center gap-4 text-center">
          <p className="text-[11px] font-bold uppercase leading-[18px] tracking-[2.5px] text-[#94A3B8] sm:text-[12px]">
            A MORE INTELLIGENT TOMORROW
          </p>
          <h2 className="text-[28px] sm:text-[34px] md:text-[38px] lg:text-[42px] font-bold leading-[1.25] text-[#0F172A] tracking-tight">
            <span className="block md:whitespace-nowrap">
              What if your software didn’t just run your business?
            </span>
            <span className="block md:whitespace-nowrap mt-1">
              What if it could think with it?
            </span>
          </h2>
        </div>

        {/* 5-Step Evolution Cards with connecting arrows */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:flex xl:items-stretch xl:justify-between xl:gap-2">
          {STEPS_DATA.map((s, i) => (
            <React.Fragment key={s.title}>
              <StepCard s={s} />

              {/* Arrow divider on desktop (xl) */}
              {i < STEPS_DATA.length - 1 && (
                <div className="hidden shrink-0 items-center justify-center px-1 xl:flex">
                  <ArrowRight
                    className="h-5 w-5 text-[#CBD5E1]"
                    strokeWidth={2}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Full Uncropped 3D Dark Success Story Banner with Distinct Silver/Gray Outline */}
        <div className="relative mt-16 sm:mt-20 w-full overflow-hidden rounded-[28px] sm:rounded-[36px] border-[2px] border-[#94A3B8]/80 shadow-[0_20px_50px_rgba(15,23,42,0.18)] bg-[#010915] md:aspect-[1024/280] min-h-[320px] flex items-center justify-center">
          {/* Full Uncut Background Image */}
          <img
            src="/images/tomorrow-banner-bg.png"
            alt="AI Success Story"
            className="absolute inset-0 h-full w-full object-cover object-center pointer-events-none"
          />

          {/* Center Banner Content matching exact screenshot */}
          <div className="relative z-10 flex w-full flex-col items-center justify-center gap-3.5 sm:gap-4 px-6 py-10 text-center sm:px-12 sm:py-12">
            {/* Eyebrow Tag (Clean plain uppercase text) */}
            <p className="text-[11px] sm:text-[12px] md:text-[12.5px] font-semibold uppercase tracking-[2.5px] text-[#94A3B8]">
              YOUR BUSINESS. YOUR OPPORTUNITY.
            </p>

            {/* Headline with exact line breaks */}
            <h2 className="text-[26px] sm:text-[34px] md:text-[40px] lg:text-[44px] font-bold leading-[1.18] tracking-tight text-white">
              <span className="block whitespace-nowrap">Your business could be our</span>
              <span className="block whitespace-nowrap">
                next <span className="text-[#00D2EE]">success story.</span>
              </span>
            </h2>

            {/* Description Paragraph */}
            <p className="max-w-[560px] text-[13px] sm:text-[14px] md:text-[15px] leading-relaxed text-[#CBD5E1]">
              Tell us where you want to go. We’ll help you discover where AI can
              create meaningful impact across your business.
            </p>

            {/* Single Vibrant Gradient Pill Button */}
            <div className="pt-2 sm:pt-3">
              <button
                onClick={openAgent}
                className="group flex items-center gap-2.5 rounded-full bg-gradient-to-r from-[#00D2EE] to-[#2F6DF6] px-8 py-3.5 text-[14px] sm:text-[15px] font-medium text-white shadow-[0_8px_30px_rgba(47,109,246,0.45),0_2px_12px_rgba(0,210,238,0.35)] transition-all duration-300 hover:scale-105 hover:shadow-[0_12px_40px_rgba(47,109,246,0.6),0_4px_16px_rgba(0,210,238,0.5)] active:scale-95 sm:px-9 sm:py-4"
              >
                <span>Discover Your AI Opportunities</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TomorrowSection;